import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Chip } from '@cowoo/shared/ui-web';
import { ApiProblem, formatDate, formatTime, qk } from '@cowoo/shared';
import type { paths } from '@cowoo/api-client';
import { api } from '../../lib/api';
import { useAdminStore } from '../../lib/store';
import { Panel, PanelRow } from '../../ui';
import { STATUS_TONE } from './Enquiries';

const STATUSES = ['new', 'in_progress', 'won', 'lost'] as const;

/** `Button` renders a `<button>`; these three navigate, so they are anchors. */
const LINK_BUTTON =
  'border-line-strong text-ink font-body rounded-buttonSm inline-flex h-9 flex-1 items-center justify-center border bg-white text-sm font-semibold hover:bg-fog';

/**
 * The enquiry detail panel (10-web-admin § 11).
 *
 * Status, assignee and notes all go through one `PATCH`, each field optional —
 * so picking an assignee does not silently rewrite a status somebody else
 * changed in the meantime.
 *
 * "Vertrag anlegen" only appears for an `office` enquiry and only for an owner:
 * office contracts are owner territory (`06-auth` § 4.2), and offering staff a
 * button the server refuses is worse than not offering it.
 */
export function EnquiryPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const can = useAdminStore((state) => state.can);
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  const enquiry = useQuery({
    queryKey: qk.admin.area('enquiries', { id }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/enquiries/{id}', { params: { path: { id } } });
      if (error) throw error;
      return data;
    },
  });

  const admins = useQuery({
    queryKey: qk.admin.area('admin-users'),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/admin-users');
      if (error) throw error;
      return data;
    },
  });

  const patch = useMutation({
    mutationFn: async (body: {
      status?: (typeof STATUSES)[number];
      assignedTo?: string | null;
      note?: string;
    }) => {
      const { error } = await api.PATCH('/admin/enquiries/{id}', { params: { path: { id } }, body });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('enquiries') });
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('nav') });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const data = enquiry.data;

  return (
    <Panel
      label={t('admin.enquiries.panelLabel')}
      title={data?.company ?? data?.name ?? '—'}
      subtitle={
        data
          ? `${data.name} · ${t(`admin.enquiries.interest.${data.interest}`)}${
              data.persons ? ` · ${t('admin.enquiries.personsN', { count: data.persons })}` : ''
            }`
          : undefined
      }
      onClose={onClose}
    >
      {!data ? null : (
        <>
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

          <div className="flex gap-2">
            <a href={`mailto:${data.email}`} className={LINK_BUTTON}>
              {t('admin.enquiries.email')}
            </a>
            {data.phone ? (
              <a href={`tel:${data.phone}`} className={LINK_BUTTON}>
                {t('admin.enquiries.call')}
              </a>
            ) : null}
            {/* Hidden until an owner has filled in `site.hubspot_portal_id`:
                without it the server cannot build a URL, and a dead button is
                worse than no button. */}
            {data.hubspotDealUrl ? (
              <a href={data.hubspotDealUrl} target="_blank" rel="noreferrer noopener" className={LINK_BUTTON}>
                {t('admin.enquiries.hubspot')}
              </a>
            ) : null}
          </div>

          <div>
            <PanelRow
              label={t('admin.enquiries.column.received')}
              value={`${formatDate(data.createdAt)}, ${formatTime(data.createdAt)}`}
            />
            <PanelRow label={t('admin.enquiries.email')} value={data.email} />
            <PanelRow label={t('admin.enquiries.phone')} value={data.phone ?? '—'} />
            <PanelRow label={t('admin.enquiries.column.fromWhen')} value={data.fromWhen ?? '—'} />
            <PanelRow label={t('admin.enquiries.firstTouch')} value={firstTouch(data.firstTouch) ?? '—'} />
          </div>

          {data.message ? (
            <div>
              <p className="text-label text-muted pb-1.5 uppercase">{t('admin.enquiries.message')}</p>
              <p className="bg-fog rounded-input text-adminBody text-text whitespace-pre-wrap px-3 py-2.5">
                {data.message}
              </p>
            </div>
          ) : null}

          <div className="flex gap-2.5">
            <label className="flex flex-1 flex-col gap-1.5">
              <span className="text-label text-muted uppercase">{t('admin.enquiries.column.status')}</span>
              <select
                value={data.status}
                onChange={(event) =>
                  patch.mutate({ status: event.target.value as (typeof STATUSES)[number] })
                }
                className="border-line-strong rounded-input text-adminBody h-10 border bg-white px-3 font-semibold"
              >
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`admin.enquiries.filter.${status}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-1 flex-col gap-1.5">
              <span className="text-label text-muted uppercase">{t('admin.enquiries.column.assignee')}</span>
              <select
                value={data.assignedTo ?? ''}
                onChange={(event) => patch.mutate({ assignedTo: event.target.value || null })}
                className="border-line-strong rounded-input text-adminBody h-10 border bg-white px-3 font-semibold"
              >
                <option value="">{t('admin.enquiries.unassigned')}</option>
                {(admins.data?.items ?? []).map((admin) => (
                  <option key={admin.userId} value={admin.userId}>
                    {admin.name ?? admin.userId}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Chip tone={STATUS_TONE[data.status]} label={t(`admin.enquiries.filter.${data.status}`)} />
            {data.interest === 'office' && can('contracts.office') ? (
              <Link
                to="/vertraege/neu"
                className="bg-teal font-body rounded-buttonSm inline-flex h-9 items-center px-3.5 text-sm font-semibold text-white"
              >
                {t('admin.enquiries.createContract')}
              </Link>
            ) : null}
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-1.5">
            <p className="text-label text-muted uppercase">{t('admin.enquiries.notes')}</p>
            {data.notes.length === 0 ? (
              <p className="text-adminSmall text-muted">{t('admin.enquiries.noNotes')}</p>
            ) : (
              <ul className="text-adminSmall text-muted flex flex-col gap-1">
                {data.notes.map((entry, index) => (
                  <li key={`${entry.at}-${index}`}>
                    {formatDate(entry.at)} {formatTime(entry.at)}
                    {entry.byName ? ` · ${entry.byName}` : ''} — {entry.text}
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2 pt-1">
              <input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder={t('admin.enquiries.addNote')}
                className="border-line-strong rounded-input text-adminBody h-10 flex-1 border px-3"
              />
              <Button
                size="sm"
                disabled={note.trim().length === 0}
                loading={patch.isPending}
                onClick={() => patch.mutate({ note: note.trim() })}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}

/**
 * "google · cpc · loft-start" from whatever the first touch actually carried.
 *
 * The parameter type is read off the endpoint rather than restated, so a change
 * to what the server sends is a compile error here instead of a line that
 * quietly renders nothing — which is exactly what the first version did.
 */
type EnquiryDetail = paths['/admin/enquiries/{id}']['get']['responses']['200']['content']['application/json'];

function firstTouch(touch: EnquiryDetail['firstTouch']): string | null {
  if (!touch) return null;
  return [touch.channel, touch.medium, touch.campaign].filter(Boolean).join(' · ') || null;
}
